import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import { createPaymentSummaryReportDownload, getPaymentSummaryReport } from '../../../../APICalling/APICalling';
import Loader from '../../../../Components/Loader/Loader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import ReportHeader from '../ReportHeader/ReportHeader';
import { calculateReportDateRange, getPeriodText, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { attachSharedUserChannelListener } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';
import styles from './PaymentSummaryReport.module.css';

const PaymentSummaryReport = ({ filterParams }) => {
    const { userAssignedOutlets } = useGlobalContext();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(null);
    const [outletName, setOutletName] = useState('');
    const [periodText, setPeriodText] = useState('');

    const getEligibleOutlets = useCallback(() => {
        const editableOutlets = (userAssignedOutlets || []).filter((o) => o.mode === 'edit');
        return editableOutlets.length > 0 ? editableOutlets : userAssignedOutlets || [];
    }, [userAssignedOutlets]);

    const getOutletIds = useCallback(
        (value) => {
            if (Array.isArray(value)) return value.map((id) => String(id).trim()).filter(Boolean);
            if (value === undefined || value === null) {
                return getEligibleOutlets().map((outlet) => String(outlet.id));
            }
            const raw = String(value).trim();
            if (!raw) {
                return [];
            }
            return raw.split(',').map((id) => id.trim()).filter(Boolean);
        },
        [getEligibleOutlets]
    );

    // Get outlet name
    useEffect(() => {
        if (!userAssignedOutlets?.length) return;
        const outletIds = getOutletIds(filterParams?.outlet);
        if (!outletIds.length) {
            setOutletName('Unknown');
            return;
        }

        const eligibleOutlets = getEligibleOutlets();
        const names = outletIds
            .map((id) => eligibleOutlets.find((o) => String(o.id) === String(id))?.name)
            .filter(Boolean);
        setOutletName(names.length ? names.join(', ') : 'Unknown');
    }, [filterParams?.outlet, getEligibleOutlets, getOutletIds, userAssignedOutlets]);

    const formatCurrency = (value) => {
        const num = parseFloat(value || 0);
        if (Number.isNaN(num)) return '$ 0.00';
        return `$ ${num.toFixed(2)}`;
    };

    const fetchPaymentSummary = useCallback(async () => {
        if (!filterParams || !filterParams.date) {
            setRows([]);
            setTotal(null);
            return;
        }

        const hasOutletFilter = Object.prototype.hasOwnProperty.call(filterParams, 'outlet');
        const outletIds = getOutletIds(filterParams?.outlet);

        setLoading(true);
        try {
            const { startDate, endDate } = calculateReportDateRange(filterParams); // "DD-MM-YYYY HH:mm"

            const params = {
                start_date: startDate,
                end_date: endDate,
                pagination: 0,
            };
            if (hasOutletFilter) {
                params.outlet_ids = outletIds.join(',');
            }

            setPeriodText(getPeriodText(filterParams));

            const response = await getPaymentSummaryReport(params);
            const isSuccess = response?.data?.success ?? response?.success ?? false;

            if (isSuccess) {
                const dataList = Array.isArray(response?.data?.data?.list)
                    ? response.data.data.list
                    : [];
                const totalObj = response?.data?.data?.total || null;

                setRows(dataList);
                setTotal(totalObj);
            } else {
                setRows([]);
                setTotal(null);
                toast.error(response?.data?.message || response?.message || 'Failed to fetch payment summary');
            }
        } catch (error) {
            console.error('Error fetching payment summary:', error);
            setRows([]);
            setTotal(null);
            toast.error(error?.response?.data?.message || error?.message || 'An error occurred while fetching payment summary');
        } finally {
            setLoading(false);
        }
    }, [filterParams, getOutletIds]);

    useEffect(() => {
        if (filterParams && filterParams.date) {
            fetchPaymentSummary();
        }
    }, [filterParams, fetchPaymentSummary]);

    const handleDownloadAll = async () => {
        if (!rows || rows.length === 0) {
            toast.warning('No data available to download');
            return;
        }

        if (!isReportRangeWithinMonths(filterParams, 3)) {
            toast.error('Please select a date range within 90 days to proceed.');
            return;
        }

        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('token');

        if (!userId) {
            toast.error('User ID not found');
            return;
        }

        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        // Same socket pattern as Sales Summary
        const channelName = `user.${userId}`;
        const eventName = 'PaymentSummaryReportReady';
        let channel;
        let detach = null;
        let toastId = null;

        setDownloadLoading(true);
        toastId = toast.loading('Downloading in progress...');

        try {
            // Join shared user channel — do not Echo.leave (payroll also uses user.{id}).

            const cleanup = () => {
                try {
                    detach?.();
                } catch (e) {}
                if (toastId) {
                    toast.dismiss(toastId);
                    toastId = null;
                }
            };

            const { channel: joinedChannel, detach: detachListener } = attachSharedUserChannelListener(
                channelName,
                eventName,
                async (event) => {
                    try {
                        const downloadUrl =
                            event?.url ||
                            event?.data?.url;

                        if (!downloadUrl) {
                            toast.error('Download URL not received. Please try again.');
                            cleanup();
                            setDownloadLoading(false);
                            return;
                        }

                        try {
                            await downloadFileWithAuth(
                                downloadUrl,
                                getDownloadFilename(event, downloadUrl, 'payment-summary.csv')
                            );
                            toast.success('Downloaded successfully');
                        } catch (downloadError) {
                            toast.error('Failed to start download');
                        }

                        cleanup();
                        setDownloadLoading(false);
                    } catch (error) {
                        toast.error('Failed to process download');
                        cleanup();
                        setDownloadLoading(false);
                    }
                }
            );
            channel = joinedChannel;
            detach = detachListener;

            if (typeof channel.error === 'function') {
                channel.error(() => {
                    toast.error('Socket connection error');
                    cleanup();
                    setDownloadLoading(false);
                });
            }

            // Wait for subscription to be ready before calling API
            await new Promise((resolve) => setTimeout(resolve, 500));

            const hasOutletFilter = Object.prototype.hasOwnProperty.call(filterParams || {}, 'outlet');
            const outletIds = getOutletIds(filterParams?.outlet);
            const { startDate, endDate } = calculateReportDateRange(filterParams);
            const downloadParams = {
                start_date: startDate,
                end_date: endDate,
                user_id: userId,
            };
            if (hasOutletFilter) {
                downloadParams.outlet_ids = outletIds.join(',');
            }

            const createResponse = await createPaymentSummaryReportDownload(downloadParams);

            const responseMessage = createResponse?.data?.data?.message || createResponse?.data?.message || createResponse?.message || '';

            const status = createResponse?.data?.status || createResponse?.status || createResponse?.data?.data?.status;

            const successFlag = createResponse?.data?.data?.success || createResponse?.data?.success || createResponse?.success || false;

            const isOk =
                Boolean(successFlag) ||
                status === 'processing' ||
                status === 'queued' ||
                status === 'success';

            if (isOk) {
                return; // Wait for socket event
            }

            const backendError =
                responseMessage ||
                'Failed to initiate download';

            toast.error(backendError);
            cleanup();
            setDownloadLoading(false);
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'An error occurred while preparing download';

            toast.error(errorMessage, { autoClose: 5000 });

            if (toastId) toast.dismiss(toastId);
            setDownloadLoading(false);

            try {
                detach?.();
            } catch (e) {}
        }
    };

    return (
        <div className={styles.paymentSummaryContainer}>
            <ReportHeader
                outletName={outletName}
                periodText={periodText}
                onDownload={handleDownloadAll}
                downloadLoading={downloadLoading}
                hasData={rows && rows.length > 0}
                downloadButtonText="Download CSV"
            />

            {loading ? (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            ) : rows && rows.length > 0 ? (
                <div className={styles.tableWrapper}>
                    <table className={styles.paymentTable}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr>
                                <th className={styles.colMethod}>Payment Method</th>
                                <th className={styles.colPayments}># Payments</th>
                                <th className={styles.colRefunds}># Refunds</th>
                                <th className={styles.colAmount}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item, index) => (
                                <tr key={`${item?.payment_method || 'method'}-${index}`}>
                                    <td className={styles.colMethod}>{item?.payment_method || 'N/A'}</td>
                                    <td className={styles.colPayments}>{item?.payments ?? 0}</td>
                                    <td className={styles.colRefunds}>{item?.refunds ?? 0}</td>
                                    <td className={styles.colAmount}>{formatCurrency(item?.amount)}</td>
                                </tr>
                            ))}

                            {total && (
                                <tr className={styles.totalRow}>
                                    <td className={styles.colMethod}>{total?.payment_method || 'Total'}</td>
                                    <td className={styles.colPayments}>{total?.payments ?? 0}</td>
                                    <td className={styles.colRefunds}>{total?.refunds ?? 0}</td>
                                    <td className={styles.colAmount}>{formatCurrency(total?.amount)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.noData}>
                    <Placeholder
                        icon={<SearchNotFoundIcon width="24" height="24" color="white" />}
                        heading="No payment summary data found"
                        para="Please try changing the filters"
                    />
                </div>
            )}
        </div>
    );
};

export default PaymentSummaryReport;

